import { useMemo, useState } from 'react';
import { Link as RouterLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Avatar,
  Box,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import ApartmentIcon from '@mui/icons-material/Apartment';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import NotificationsIcon from '@mui/icons-material/Notifications';
import DescriptionIcon from '@mui/icons-material/Description';
import CloudIcon from '@mui/icons-material/Cloud';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HistoryIcon from '@mui/icons-material/History';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import PolicyIcon from '@mui/icons-material/Policy';
import PaymentIcon from '@mui/icons-material/Payment';
import ExtensionIcon from '@mui/icons-material/Extension';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import LockIcon from '@mui/icons-material/Lock';
import BuildCircleIcon from '@mui/icons-material/BuildCircle';
import SchemaIcon from '@mui/icons-material/Schema';
import StorageIcon from '@mui/icons-material/Storage';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SmsIcon from '@mui/icons-material/Sms';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import SecurityIcon from '@mui/icons-material/Security';
import { useTranslation } from 'react-i18next';
import { NotificationBell } from '../components/NotificationBell';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { useAuth } from '../auth/AuthContext';
import { hasPermission, isInternalUser, isSuperadminOrDeveloper } from '../auth/permissions';
import { colorTokens, layoutTokens, radiusTokens } from '../app/designTokens';
import {
  sidebarNavIconSx,
  sidebarNavItemSx,
  sidebarNavTextProps,
} from './sidebarNavStyles';
import mafertSystemLogoWhite from '../assets/brand/mafert-system-logo-white.svg';
import mafertSymbolWhite from '../assets/brand/mafert-symbol-white.svg';
import { getSystemNavSections } from './systemNavConfig';

/** Exact list routes that opt into the wider data-list content rail. */
const DATA_LIST_RAIL_PATHS = new Set([
  '/customers',
  '/tenants',
  '/onboarding-intents',
  '/provisioning/readiness',
  '/support',
  '/billing/invoices',
  '/billing/module-prices',
  '/billing/sms-usage',
  '/billing/permission-cleanup',
  '/payments',
  '/subscriptions',
  '/billing/subscription-changes',
  '/system-modules/mafertapp',
  '/tenant-access',
  '/setup-wizard',
  '/setup/module-profiles',
  '/tenant-provisioning',
  '/stripe/webhooks',
  '/stripe/sync-logs',
  '/external-providers',
  '/external-provider-health',
  '/features/modules',
  '/features',
  '/entitlements/evaluations',
]);

const NAV_ICONS = {
  dashboard: <DashboardIcon fontSize="small" />,
  customers: <PeopleIcon fontSize="small" />,
  tenants: <ApartmentIcon fontSize="small" />,
  trial: <RocketLaunchIcon fontSize="small" />,
  onboarding: <HowToRegIcon fontSize="small" />,
  provisioning: <FactCheckIcon fontSize="small" />,
  support: <SupportAgentIcon fontSize="small" />,
  notifications: <NotificationsIcon fontSize="small" />,
  billing: <ReceiptLongIcon fontSize="small" />,
  modulePricing: <CreditCardIcon fontSize="small" />,
  invoices: <ReceiptIcon fontSize="small" />,
  payments: <PaymentIcon fontSize="small" />,
  subscriptions: <SubscriptionsIcon fontSize="small" />,
  subscriptionChanges: <SwapHorizIcon fontSize="small" />,
  smsUsage: <SmsIcon fontSize="small" />,
  permissionCleanup: <SecurityIcon fontSize="small" />,
  mafertappModules: <ExtensionIcon fontSize="small" />,
  tenantAccess: <LockIcon fontSize="small" />,
  setupWizard: <BuildCircleIcon fontSize="small" />,
  setupProfiles: <BuildCircleIcon fontSize="small" />,
  tenantProvisioning: <StorageIcon fontSize="small" />,
  tenantSchema: <SchemaIcon fontSize="small" />,
  stripe: <PaymentIcon fontSize="small" />,
  externalProviders: <CloudIcon fontSize="small" />,
  providerHealth: <MonitorHeartIcon fontSize="small" />,
  featureModules: <ExtensionIcon fontSize="small" />,
  features: <ToggleOnIcon fontSize="small" />,
  entitlements: <HistoryEduIcon fontSize="small" />,
  notificationTemplates: <DescriptionIcon fontSize="small" />,
  recycleBin: <DeleteOutlineIcon fontSize="small" />,
  lifecycleEvents: <HistoryIcon fontSize="small" />,
  purgeJobs: <CleaningServicesIcon fontSize="small" />,
  retentionPolicies: <PolicyIcon fontSize="small" />,
};

const { semantic } = colorTokens;

function isNavSelected(pathname, itemPath) {
  if (itemPath === '/') {
    return pathname === '/';
  }

  // Preferences is nested under Notifications, not a top-level nav item.
  if (
    (pathname === '/notification-preferences' || pathname === '/portal/notification-preferences')
    && (itemPath === '/notifications' || itemPath === '/portal/notifications')
  ) {
    return true;
  }

  return pathname === itemPath || pathname.startsWith(`${itemPath}/`);
}

function isNavItemVisible(item, session) {
  if (item.internalOnly && !isSuperadminOrDeveloper(session)) {
    return false;
  }

  if (item.permission && !hasPermission(session, item.permission)) {
    return false;
  }

  return true;
}

function getUserDisplayName(session) {
  return session?.user?.name || session?.user?.full_name || session?.user?.email || 'User';
}

function getUserInitials(session) {
  const name = getUserDisplayName(session);
  const parts = String(name).trim().split(/\s+/).filter(Boolean);

  if (parts.length === 0) {
    return 'U';
  }

  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }

  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

function findActiveNavLabel(navSections, pathname) {
  for (const section of navSections) {
    for (const item of section.items) {
      if (isNavSelected(pathname, item.path)) {
        return item.label;
      }
    }
  }

  return null;
}

export function SystemLayout() {
  const { t } = useTranslation(['navigation', 'common']);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const { session, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const isDesktopExpanded = useMediaQuery(`(min-width:${layoutTokens.sidebarCollapseMaxWidth + 1}px)`);
  const isCollapsedRail = useMediaQuery(
    `(min-width:${layoutTokens.sidebarCollapseMinWidth}px) and (max-width:${layoutTokens.sidebarCollapseMaxWidth}px)`,
  );
  const showPermanentSidebar = isDesktopExpanded || isCollapsedRail;
  const isMobile = !showPermanentSidebar;
  const collapsed = isCollapsedRail;
  const sidebarWidth = collapsed ? layoutTokens.sidebarCollapsedWidth : layoutTokens.sidebarWidth;
  const contentMaxWidth = DATA_LIST_RAIL_PATHS.has(location.pathname)
    ? layoutTokens.contentMaxWidthDataList
    : layoutTokens.contentMaxWidth;

  const navSections = useMemo(() => {
    if (!isInternalUser(session)) {
      return [];
    }

    return getSystemNavSections(t).filter((section) => {
      if (section.internalOnly && !isSuperadminOrDeveloper(session)) {
        return false;
      }

      return section.items.some((item) => isNavItemVisible(item, session));
    });
  }, [session, t]);

  const activeLabel = findActiveNavLabel(navSections, location.pathname) || t('navigation:dashboard');
  // Navbar-only routes (not in sidebar). Do not fall back to Dashboard / Prehľad.
  const breadcrumb = location.pathname === '/notification-preferences'
    ? `${t('navigation:admin')} / ${t('navigation:notifications')} / ${t('navigation:notification_preferences')}`
    : location.pathname === '/profile'
      ? `${t('navigation:admin')} / ${t('common:profile')}`
      : location.pathname === '/account/security'
        ? `${t('navigation:admin')} / ${t('navigation:account_security')}`
        : `${t('navigation:admin')} / ${activeLabel}`;
  const displayName = getUserDisplayName(session);
  const initials = getUserInitials(session);

  const topbarActionSx = {
    width: 40,
    height: 40,
    borderRadius: `${radiusTokens.iconButton}px`,
    border: `1px solid ${semantic.border.default}`,
    bgcolor: semantic.action.secondary.bg,
    color: semantic.text.primary,
    '&:hover': {
      bgcolor: semantic.bg.surface,
    },
  };

  const renderNavSections = (compact) => (
    <Box
      sx={{
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        px: compact ? 1 : 1.5,
        py: 1,
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        '&::-webkit-scrollbar': {
          width: 0,
          height: 0,
        },
      }}
    >
      {navSections.map((section) => {
        const visibleItems = section.items.filter((item) => isNavItemVisible(item, session));

        if (visibleItems.length === 0) {
          return null;
        }

        return (
          <Box key={section.id} sx={{ mb: 1.5 }}>
            {section.label && !compact && (
              <Typography
                variant="caption"
                sx={{
                  px: 1,
                  pt: 1,
                  pb: 0.75,
                  display: 'block',
                  fontWeight: 600,
                  letterSpacing: '0.24px',
                  textTransform: 'uppercase',
                  color: semantic.text.muted,
                }}
              >
                {section.label}
              </Typography>
            )}
            <List dense disablePadding>
              {visibleItems.map((item) => {
                const selected = isNavSelected(location.pathname, item.path);
                const button = (
                  <ListItemButton
                    component={RouterLink}
                    to={item.path}
                    selected={selected}
                    onClick={() => setMobileOpen(false)}
                    sx={sidebarNavItemSx({ compact, selected })}
                  >
                    <ListItemIcon sx={sidebarNavIconSx({ compact, selected })}>
                      {NAV_ICONS[item.icon]}
                    </ListItemIcon>
                    {!compact && (
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={sidebarNavTextProps({
                          selected,
                          fontSize: 12,
                        })}
                      />
                    )}
                  </ListItemButton>
                );

                if (compact) {
                  return (
                    <Tooltip key={item.path} title={item.label} placement="right">
                      <Box>{button}</Box>
                    </Tooltip>
                  );
                }

                return <Box key={item.path}>{button}</Box>;
              })}
            </List>
          </Box>
        );
      })}
    </Box>
  );

  const drawerContent = (compact) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: compact ? 'center' : 'flex-start',
          px: compact ? 1.5 : '20px',
          pt: compact ? 1.5 : '18px',
          pb: compact ? 1.5 : '18px',
          boxSizing: 'border-box',
          flexShrink: 0,
        }}
      >
        {compact ? (
          <Box
            component="img"
            src={mafertSymbolWhite}
            alt="Mafert"
            sx={{
              width: 36,
              height: 36,
              objectFit: 'contain',
              display: 'block',
              flexShrink: 0,
            }}
          />
        ) : (
          <Box
            component="img"
            src={mafertSystemLogoWhite}
            alt="Mafert System"
            sx={{
              width: 130,
              maxWidth: '100%',
              maxHeight: 52,
              height: 'auto',
              objectFit: 'contain',
              objectPosition: 'left center',
              display: 'block',
            }}
          />
        )}
      </Box>

      {renderNavSections(compact)}

      <Box sx={{ px: compact ? 1 : 2, pb: 2 }}>
        <Divider sx={{ borderColor: semantic.border.subtle, mb: 1.5 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            sx={{
              width: 36,
              height: 36,
              fontSize: 13,
              fontWeight: 700,
              bgcolor: semantic.status.brand.bg,
              border: `1px solid ${semantic.status.brand.border}`,
              color: semantic.status.brand.text,
            }}
          >
            {initials}
          </Avatar>
          {!compact && (
            <Box sx={{ minWidth: 0 }}>
              <Typography
                noWrap
                sx={{ fontSize: 13, fontWeight: 600, color: semantic.text.primary }}
              >
                {displayName}
              </Typography>
              <Typography
                noWrap
                sx={{ fontSize: 12, color: semantic.text.muted, letterSpacing: '0.24px' }}
              >
                {t('navigation:admin')}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );

  const handleLogout = async () => {
    setAnchorEl(null);
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: semantic.bg.canvas }}>
      <AppBar
        position="fixed"
        sx={{
          width: showPermanentSidebar ? `calc(100% - ${sidebarWidth}px)` : '100%',
          ml: showPermanentSidebar ? `${sidebarWidth}px` : 0,
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar
          sx={{
            gap: 2,
            px: { xs: 2, md: 3 },
            minHeight: `${layoutTokens.topbarHeight}px !important`,
          }}
        >
          {isMobile && (
            <IconButton
              edge="start"
              onClick={() => setMobileOpen(!mobileOpen)}
              sx={topbarActionSx}
              aria-label="Open navigation"
            >
              <MenuIcon fontSize="small" />
            </IconButton>
          )}

          <Typography
            noWrap
            sx={{
              flexShrink: 0,
              maxWidth: { xs: 120, sm: 240, md: 280 },
              fontSize: 13,
              color: semantic.text.secondary,
            }}
          >
            {breadcrumb}
          </Typography>

          <Box sx={{ flexGrow: 1 }} />

          <TextField
            size="small"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder={t('navigation:search_admin')}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: semantic.text.muted, fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: 160, sm: 240, md: 300 },
              '& .MuiOutlinedInput-root': {
                height: 40,
                borderRadius: `${radiusTokens.search}px`,
                bgcolor: semantic.bg.input,
                color: semantic.text.primary,
                '& fieldset': {
                  borderColor: semantic.border.default,
                },
                '&:hover fieldset': {
                  borderColor: semantic.border.default,
                },
                '&.Mui-focused fieldset': {
                  borderColor: semantic.border.default,
                },
              },
              '& .MuiInputBase-input::placeholder': {
                color: semantic.text.muted,
                opacity: 1,
              },
            }}
          />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ '& .MuiIconButton-root': topbarActionSx }}>
              <NotificationBell
                notificationsPath="/notifications"
                preferencesPath="/notification-preferences"
              />
            </Box>
            <Box sx={{ '& .MuiIconButton-root': topbarActionSx }}>
              <LanguageSwitcher persistToApi />
            </Box>
            <IconButton
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={topbarActionSx}
              aria-label={t('common:profile')}
            >
              <AccountCircleIcon fontSize="small" />
            </IconButton>
          </Box>

          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
            <MenuItem disabled>
              <Typography variant="body2">{session?.user?.email}</Typography>
            </MenuItem>
            <MenuItem component={RouterLink} to="/profile" onClick={() => setAnchorEl(null)}>
              {t('common:profile')}
            </MenuItem>
            <MenuItem component={RouterLink} to="/account/security" onClick={() => setAnchorEl(null)}>
              {t('navigation:account_security')}
            </MenuItem>
            <MenuItem onClick={handleLogout}>{t('common:logout')}</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{
          width: showPermanentSidebar ? sidebarWidth : 0,
          flexShrink: 0,
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: isMobile ? 'block' : 'none',
            '& .MuiDrawer-paper': {
              width: layoutTokens.sidebarWidth,
              boxSizing: 'border-box',
              bgcolor: semantic.bg.sidebar,
              backgroundImage: 'none',
              borderRight: `1px solid ${semantic.border.subtle}`,
            },
          }}
        >
          {drawerContent(false)}
        </Drawer>

        <Drawer
          variant="permanent"
          open
          sx={{
            display: showPermanentSidebar ? 'block' : 'none',
            '& .MuiDrawer-paper': {
              width: sidebarWidth,
              boxSizing: 'border-box',
              bgcolor: semantic.bg.sidebar,
              backgroundImage: 'none',
              borderRight: `1px solid ${semantic.border.subtle}`,
              overflowX: 'hidden',
            },
          }}
        >
          {drawerContent(collapsed)}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: showPermanentSidebar ? `calc(100% - ${sidebarWidth}px)` : '100%',
          minHeight: '100vh',
          bgcolor: semantic.bg.canvas,
          pt: `${layoutTokens.topbarHeight}px`,
        }}
      >
        <Box
          sx={{
            maxWidth: contentMaxWidth,
            width: '100%',
            mx: 'auto',
            px: { xs: 2, md: 4 },
            py: 3,
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
